import React, { FC, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Form } from '@components/atoms/Form';
import { Input, InputTypes, InputVariants } from '@components/atoms/Input';
import { CheckboxFilter } from '../CheckboxFilter';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { SelectFilter } from '../SelectFilter';
import { useData } from '@hooks/useData';
import { useFilters } from '@hooks/useFilters';
import { useFilterBuilder } from '@hooks/useFilterBuilder';
import { useFilterBuilderText } from '@hooks/useFilterBuilderText';
import { useGetStores } from '@services/useGetStores';
import { useGetAllCategoriesWithProducts } from '@services/useGetAllCategoriesWithProducts';
import { useGetAllServicesCategories } from '@services/useGetAllServicesCategories';
import { useGetZones } from '@services/useGetZones';
import { Modules, useModule } from '@hooks/useModule';
import { ServiceCardProps } from '@components/molecules/ServiceCard';
import { FavoriteFilter } from '../FavoriteFilter';

interface FilterSidebarMerchantsProps {
    pageToRequest: number;
    checkPageToRequest: () => void;
}

export const FilterSidebarMerchants: FC<FilterSidebarMerchantsProps> = ({ pageToRequest, checkPageToRequest }) => {
    const location = useLocation();
    const { login, stores, isLoadingStores, setStores } = useData();
    const { request: requestStores } = useGetStores();
    const { isModuleActive } = useModule();
    const { appliedFilters, results, setResults, currentPage, setCurrentPage } = useFilters();

    useEffect(() => {
        // when unmounting page, reset results
        // to prevent showing old results when returning to this page
        return () => {
            setResults([]);
            setStores(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formRef = useRef<HTMLFormElement>(null);

    const queryParams = new URLSearchParams(location.search);
    const getTextParam = queryParams.get('text') || '';
    const getCategoriesParam = queryParams.get('categories') || '';
    const getZonesParam = queryParams.get('zones') || '';
    const getTypeParam = queryParams.get('type') || '';
    const getOrderParam = queryParams.get('order') || '';
    const favoriteParam = queryParams.get('favorites') === 'favorite-only';

    const fetchStores = (page: number) => {
        setCurrentPage(page);

        // prevent calls for pages that don't exist
        if (stores?.pagination?.total_pages && page > stores?.pagination?.total_pages) {
            return;
        }

        const categoriesUuids = getCategoriesParam ? getCategoriesParam.split(',') : [];
        const zonesUuids = getZonesParam ? getZonesParam.split(',') : [];
        const types = getTypeParam ? getTypeParam.split(',') : [];

        requestStores({
            textSearch: getTextParam,
            categories_uuid: categoriesUuids,
            zone_uuid: zonesUuids,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            type: types as any,
            page: page,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            order: getOrderParam as any,
            favorite: favoriteParam,
        });
    };

    // request more stores from next page
    useEffect(() => {
        pageToRequest > currentPage && !isLoadingStores && fetchStores(currentPage + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageToRequest]);

    // request first page whenever a filter changes
    const submitForm = () => fetchStores(1);

    useEffect(() => {
        !isLoadingStores && formRef?.current?.requestSubmit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedFilters]);

    // every time the endpoint is called, append the data to results constant
    // if the page being called is the first page, reset results
    useEffect(() => {
        setResults((previous: ServiceCardProps[]) => {
            const currentPageData = (stores?.data || []).map((item) => {
                return {
                    ...item,
                    logo: {
                        src: item.logo?.src,
                        type: item.logo?.type,
                        width: item.logo?.width,
                        height: item.logo?.height,
                        isDefaultLogo: item.logo?.is_default_logo,
                    },
                    initialFavorite: item.favorite || false,
                };
            });

            return stores?.pagination?.current_page === 1 ? currentPageData : [...previous, ...currentPageData];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stores]);

    // manually check if next page should be requested
    // in case user's screen is big and isn't possible to scroll to trigger next page request
    useEffect(() => {
        checkPageToRequest();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [results.length]);

    return (
        <Form formRef={formRef} classes="d-flex flex-column gap-4" onSubmit={submitForm}>
            <TextFilter />

            <>{isModuleActive(Modules.favorite) && login !== null && <FavoriteFilter />}</>

            <TypeFilters />

            {/* #TODO: this feature will be displayed later in development
            <SelectFilter
                title={translations.title} // -> t('filter-sidebar-merchants-component.distance.title')
                options={[
                    { value: '', text: '+ 2km' },
                    { value: '', text: '+ 5km' },
                    { value: '', text: '+ 10km' },
                ]}
                onChange={() => ...}
            /> */}

            <CategoriesFilters />

            <ZonesFilter />
        </Form>
    );
};

const TextFilter: FC = () => {
    const { appliedFilters, setFilterValues } = useFilters();
    const { t } = useTranslation();

    const translations = {
        placeholder: t('filter-sidebar-merchants-component.text-search.placeholder'),
    };

    const paramName = 'text';
    const title = 'text';

    const { search, setSearch } = useFilterBuilderText({
        paramName,
        appliedValue: appliedFilters.find((item) => item.label === title)?.values[0].name || '',
        setFilterValues: (data) => setFilterValues(title, data),
    });

    return (
        <Input
            type={InputTypes.text}
            variant={InputVariants.searchInput}
            placeholder={translations.placeholder}
            value={search || ''}
            onChange={(value) => setSearch(value)}
        />
    );
};

const TypeFilters: FC = () => {
    const { appliedFilters, setFilterValues } = useFilters();
    const { t } = useTranslation();

    const translations = {
        title: t('filter-sidebar-merchants-component.sales.title'),
        typeProducts: t('filter-sidebar-merchants-component.sales.type-products'),
        typeServices: t('filter-sidebar-merchants-component.sales.type-services'),
        typeBoth: t('filter-sidebar-merchants-component.sales.type-both'),
    };

    const paramName = 'type';
    const title = translations.title;

    const { getOptions, toggleValue } = useFilterBuilder([
        {
            paramName,
            labelField: 'name',
            data: {
                data: [
                    { name: translations.typeProducts, uuid: 'product' },
                    { name: translations.typeServices, uuid: 'service' },
                    { name: translations.typeBoth, uuid: 'both' },
                ],
            },
            requestData: null,
            appliedValues: appliedFilters.find((item) => item.label === title)?.values.map((value) => value.name) || [],
            setFilterValues: (data) => setFilterValues(title, data),
        },
    ]);

    const options = getOptions(paramName);

    return (
        <>{options && <CheckboxFilter title={title} options={options} onChange={(value) => toggleValue(paramName, value)} />}</>
    );
};

const CategoriesFilters: FC = () => {
    const { allCategoriesWithProducts, allServicesCategories } = useData();
    const { request: requestAllProductsCategories } = useGetAllCategoriesWithProducts();
    const { request: requestAllServicesCategories } = useGetAllServicesCategories();
    const { appliedFilters, setFilterValues } = useFilters();
    const { t, i18n } = useTranslation();

    const translations = {
        title: t('filter-sidebar-merchants-component.categories.title'),
    };

    // call services, if we don't have the information yet
    useEffect(() => {
        !allCategoriesWithProducts && requestAllProductsCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allCategoriesWithProducts, i18n.language]);

    useEffect(() => {
        !allServicesCategories && requestAllServicesCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allServicesCategories, i18n.language]);

    const allCategories = useMemo(() => {
        // both services must have already returned values, for filter to work
        if (allCategoriesWithProducts === null || allServicesCategories === null) {
            return [];
        }

        const productCategories = (allCategoriesWithProducts?.data || []).map((category) => ({
            ...category,
            menuCategoryType: 'product',
        }));

        const serviceCategories = (allServicesCategories?.data || []).map((category) => ({
            ...category,
            menuCategoryType: 'service',
        }));

        const combinedCategories = [...productCategories, ...serviceCategories];

        // Sort by name
        combinedCategories.sort((a, b) => {
            if ((a.name || '') < (b.name || '')) return -1;
            if ((a.name || '') > (b.name || '')) return 1;
            return 0;
        });

        return combinedCategories;
    }, [allCategoriesWithProducts, allServicesCategories]);

    const paramName = 'categories';
    const title = translations.title;

    const { getOptions, toggleValue } = useFilterBuilder(
        allCategories.length > 0
            ? [
                  {
                      paramName,
                      labelField: 'name',
                      data: { data: allCategories },
                      requestData: null,
                      appliedValues: appliedFilters.find((item) => item.label === title)?.values.map((value) => value.name) || [],
                      setFilterValues: (data) => setFilterValues(title, data),
                  },
              ]
            : []
    );

    const options = getOptions(paramName);

    return (
        <>
            {allCategories.length > 0 && options && (
                <CheckboxFilter title={title} options={options} onChange={(value) => toggleValue(paramName, value)} />
            )}
        </>
    );
};

const ZonesFilter: FC = () => {
    const { allZones } = useData();
    const { request: requestAllZones } = useGetZones();
    const { appliedFilters, setFilterValues } = useFilters();
    const { t } = useTranslation();
    const { isModuleActive } = useModule();

    const translations = {
        title: t('filter-sidebar-merchants-component.zones.title'),
    };

    useEffect(() => {
        if (!allZones && isModuleActive(Modules.zones)) {
            requestAllZones();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allZones]);

    const paramName = 'zones';
    const title = translations.title;

    const { getOptions, toggleValue } = useFilterBuilder(
        allZones
            ? [
                  {
                      paramName,
                      labelField: 'name',
                      data: { data: allZones.data || [] },
                      requestData: null,
                      appliedValues: appliedFilters.find((item) => item.label === title)?.values.map((v) => v.name) || [],
                      setFilterValues: (data) => setFilterValues(title, data),
                  },
              ]
            : []
    );

    const options = getOptions(paramName);

    return (
        <>
            {isModuleActive(Modules.zones) && allZones && options && (
                <CheckboxFilter title={title} options={options} onChange={(value) => toggleValue(paramName, value)} />
            )}
        </>
    );
};
